/* ==========================================================================
   Vinhuset cart behaviour — replaces the Rebuy Smart Cart logic on top of
   Broadcast's native cart drawer / cart page.

   1. Miljøtillæg guard  – exactly one ORDER_FEE line while the cart has products
   2. 6 × Enkelt flaske  → 1 Kasse (cheaper), with a short notice
   3. Express håndtering – add-on toggle
   4. Udskudte betalinger (Løn) – single-choice options; info buttons by both in the drawer (F-113)
   5. Keeps <details> open state across cart re-renders
   6. Post-add-to-cart offers
   7. Faster "Læg i kurv": the drawer opens at the tap, one request per add
   8. Quantity changes and "Fjern" go to the line's key, not its place in the cart
   9. The age check's answer on the order (_age_confirmed, _age_confirmed_at)
  10. "Accepter handelsbetingelser": ticked, and on the cart, as the Smart Cart does today (F-130)
   ========================================================================== */
(function () {
  'use strict';

  const vh = (window.vh = window.vh || {});
  const config = vh.config || {};
  const routes = (window.theme && window.theme.routes) || {};
  const JSON_HEADERS = {'Content-Type': 'application/json', Accept: 'application/json'};
  const serviceIds = new Set((config.serviceVariantIds || []).map(Number));
  // The drawer's line items, rendered by Shopify in the same request as a cart change (bundled section rendering)
  const DRAWER_SECTION = 'api-cart-items';

  const api = {
    async cart() {
      const res = await fetch(`${routes.cart_url || '/cart'}.js`, {headers: {Accept: 'application/json'}});
      if (!res.ok) throw new Error('cart fetch failed');
      const cart = await res.json();
      vh.lastCart = cart; // the latest cart this page knows, for other scripts (e.g. assets/vh-rebuy.js)
      return cart;
    },
    add(items, render) {
      const body = render ? {items, sections: DRAWER_SECTION, sections_url: window.location.pathname} : {items};
      return fetch(`${routes.cart_add_url || '/cart/add'}.js`, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body)});
    },
    update(updates, render) {
      const body = render ? {updates, sections: DRAWER_SECTION, sections_url: window.location.pathname} : {updates};
      return fetch(`${routes.cart_update_url || '/cart/update'}.js`, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body)});
    },
  };
  vh.cartApi = api;

  /* ------------------------------------------------------------------------
     Cart state for the checks below
     - freshCart: the cart that came with the drawer Shopify just rendered (data-vh-cart-json). It is only
       set while that drawer is being built, so the checks it triggers read exactly that cart and skip a
       /cart.js request. Every other cart change is fetched as before.
     - vh.cartSettled(): resolves once no add, fee check, six-pack merge or queued change (Express/Løn, gift note,
       "+", "−", "Fjern") is on its way. Checkout waits for it, so the customer never reaches checkout ahead of the
       cart (e.g. before the fee is in, or with an Express or Løn choice that was still being saved, F-065).
     ------------------------------------------------------------------------ */
  let freshCart = null;
  let pendingAdds = 0;
  // Changes in the write queue (queueWrite): Express/Løn, the gift note, "+", "−" and "Fjern"
  let pendingWrites = 0;
  let flushAttributeSave = null;
  let feeChecked = false;
  const settleWaiters = [];

  function readCartJson(root) {
    const el = root && root.querySelector('[data-vh-cart-json]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function drawerItems() {
    const items = document.querySelector('cart-drawer cart-items');
    return items && typeof items.build === 'function' ? items : null;
  }

  /* Build the drawer from section HTML Shopify returned with a cart change. silent = don't pop it open. */
  function buildDrawer(html, {silent = false} = {}) {
    const items = drawerItems();
    if (!items || !html) return false;
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const content = holder.querySelector('[data-api-content]');
    if (!content) return false;
    if (silent) {
      const stop = (event) => event.stopPropagation();
      window.addEventListener('theme:product:added', stop, {capture: true, once: true});
    }
    freshCart = readCartJson(content);
    if (freshCart) vh.lastCart = freshCart;
    try {
      items.build(content);
    } finally {
      freshCart = null;
    }
    return true;
  }

  function isSettled() {
    return !pendingAdds && !pendingWrites && !syncing && !queued && timer === null;
  }

  function checkSettled() {
    if (!isSettled()) return;
    while (settleWaiters.length) settleWaiters.shift()();
  }

  vh.renderedCart = () => freshCart;

  vh.cartSettled = function (timeout = 8000) {
    // A gift note still waiting for the pause in typing is saved now
    if (flushAttributeSave) flushAttributeSave();
    if (isSettled()) return Promise.resolve();
    return new Promise((resolve) => {
      settleWaiters.push(resolve);
      setTimeout(resolve, timeout); // never hold checkout back for long if Shopify is slow
    });
  };

  /* "Læg i kurv", Express/Løn, the gift note, "+", "−", "Fjern" and the fee with the answers reach Shopify one at a
     time. Of two changes sent at the same moment Shopify can keep only one: a Løn date unticked came back when "Fjern"
     was pressed 0.2 s later, Express ticked 0.1 s after "Læg i kurv" lost the Express line or the wine, and of two
     wines added 0.15 s apart only the second stayed (F-146). An add still shows at the tap (fastAddToCart): only its
     request waits its turn. Checkout waits until the queue is empty (isSettled, F-065). */
  let writeQueue = Promise.resolve();
  function queueWrite(job) {
    pendingWrites += 1;
    const run = writeQueue.then(job);
    writeQueue = run.catch(() => {});
    return run.finally(() => {
      pendingWrites -= 1;
      checkSettled();
    });
  }

  /* Re-render the cart through Broadcast. silent = don't pop the drawer open. */
  function refresh({silent = false} = {}) {
    if (silent) {
      const stop = (event) => event.stopPropagation();
      window.addEventListener('theme:product:added', stop, {capture: true, once: true});
    }
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
  }
  vh.refreshCart = refresh;

  function drawerIsOpen() {
    return !!document.querySelector('cart-drawer.is-open');
  }

  function isServiceItem(item) {
    if (serviceIds.has(Number(item.variant_id))) return true;
    return String(item.sku || '').trim().toUpperCase() === 'ORDER_FEE';
  }
  vh.isServiceItem = isServiceItem;

  function isFeeItem(item) {
    return (config.feeVariantId && Number(item.variant_id) === Number(config.feeVariantId)) || String(item.sku || '').trim().toUpperCase() === 'ORDER_FEE';
  }

  /* ------------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------------ */
  function toast(message) {
    if (!message) return;
    let holder = document.querySelector('.vh-toast');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'vh-toast';
      holder.setAttribute('role', 'status');
      holder.setAttribute('aria-live', 'polite');
      document.body.appendChild(holder);
    }
    holder.textContent = message;
    holder.classList.add('is-visible');
    clearTimeout(holder._timer);
    holder._timer = setTimeout(() => holder.classList.remove('is-visible'), 4200);
  }
  vh.toast = toast;

  /* ------------------------------------------------------------------------
     Sync pipeline (serialised, debounced)
     ------------------------------------------------------------------------ */
  let syncing = false;
  let queued = false;
  let timer = null;

  /* delay 0 right after an add (the fee should be in before the customer reaches checkout); quantity changes
     are grouped with a short pause. cart = the cart Shopify rendered with the change, if we have it. */
  function scheduleSync({silent, cart = null, delay = 180} = {}) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      runSync({silent, cart});
    }, delay);
  }

  async function runSync({silent, cart: knownCart = null} = {}) {
    if (syncing) {
      queued = true;
      return;
    }
    syncing = true;
    let changed = false;
    let drawerHtml = null;
    try {
      let cart = knownCart || (await api.cart());
      warmAgeCheck(cart);
      if (config.sixpackEnabled) {
        const merged = await mergeSixPacks(cart);
        if (merged) {
          changed = true;
          cart = await api.cart();
        }
      }
      if (config.feeEnabled && config.feeVariantId) {
        const together = await addFeeWithAnswers(cart);
        const fee = together || (await ensureFee(cart));
        if (together) {
          // Shopify's answer has the fee, the age answer and the terms: the steps below have nothing left to write
          cart = together.cart;
          feeChecked = true;
        }
        if (fee.changed) {
          changed = true;
          drawerHtml = fee.html;
        } else if (!changed) {
          feeChecked = true;
        }
      } else {
        feeChecked = true;
      }
      await documentAge(cart);
      await documentTerms(cart);
    } catch (error) {
      console.warn('[vh-cart] sync failed', error);
    } finally {
      syncing = false;
    }
    if (changed) {
      const quiet = silent !== undefined ? silent : !drawerIsOpen();
      // The fee change came back with the drawer already rendered: build it from that, no extra request
      if (!(drawerHtml && buildDrawer(drawerHtml, {silent: quiet}))) refresh({silent: quiet});
    }
    if (queued) {
      queued = false;
      scheduleSync({silent: true});
    }
    checkSettled();
  }

  /* The first product in the cart: the fee, the age answer and the terms go to Shopify in one write. One after another
     they held a quick "Til betaling" for 3-4 s after "Læg i kurv" (F-144). /cart/update.js adds the fee by its variant
     id when it isn't in the cart, sets the attributes and renders the drawer in the same request. Every other case,
     and a write Shopify refuses or answers without the fee, takes the steps one by one as before. */
  async function addFeeWithAnswers(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.some((item) => !isServiceItem(item)) || items.some(isFeeItem)) return null;
    const attributes = Object.assign({}, ageAttributes(cart), termsAttributes(cart));
    let result = null;
    await queueWrite(async () => {
      const res = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({updates: {[Number(config.feeVariantId)]: 1}, attributes, sections: DRAWER_SECTION, sections_url: window.location.pathname}),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data.items) || !data.items.some(isFeeItem)) return;
      const html = (data.sections && data.sections[DRAWER_SECTION]) || null;
      delete data.sections;
      vh.lastCart = data;
      result = {changed: true, html, cart: data};
    });
    return result;
  }

  /* 1. Miljøtillæg — same rules as the live theme (ensureOrderFee).
     Returns {changed, html}: html is the drawer Shopify rendered with the change, when it sent one. */
  async function ensureFee(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const feeItems = items.filter(isFeeItem);
    const products = items.filter((item) => !isServiceItem(item));
    const feeUnits = feeItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const sectionHtml = async (response) => {
      try {
        const data = await response.json();
        return (data && data.sections && data.sections[DRAWER_SECTION]) || null;
      } catch (e) {
        return null;
      }
    };

    if (products.length && !feeItems.length) {
      return {changed: true, html: await sectionHtml(await api.add([{id: Number(config.feeVariantId), quantity: 1}], true))};
    }
    if (!products.length && items.length) {
      /* Only service lines left (fee, Løn, express) → clear them */
      const updates = {};
      items.forEach((item) => (updates[item.key] = 0));
      return {changed: true, html: await sectionHtml(await api.update(updates, true))};
    }
    if (feeItems.length && feeUnits !== 1) {
      const updates = {};
      feeItems.forEach((item, index) => (updates[item.key] = index === 0 ? 1 : 0));
      return {changed: true, html: await sectionHtml(await api.update(updates, true))};
    }
    return {changed: false, html: null};
  }

  /* The MitID age check (snippets/mitid-age-verification) looks up each product's tags at checkout; look them up
     now, while the customer is still in the cart, so checkout only has to confirm the cart itself. */
  function warmAgeCheck(cart) {
    if (typeof window.vhMitidWarm !== 'function' || !cart || !Array.isArray(cart.items)) return;
    window.vhMitidWarm(cart.items.map((item) => item.handle).filter(Boolean));
  }

  /* 2. Six single bottles of the same wine → one case */
  const productCache = new Map();
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`${routes.root || '/'}products/${handle}.js`).then((res) => (res.ok ? res.json() : null));
    productCache.set(handle, promise);
    return promise;
  }

  // Bottle or case? The same rule as snippets/vh-variant-kind.liquid, with the store's word lists from the settings
  function variantKind(title, position) {
    const text = String(title || '').toLowerCase().trim();
    const has = (words) => (words || []).some((word) => word && text.includes(word));
    const isCase = has(config.caseWords);
    const isSingle = has(config.singleWords);
    let kind = 'other';
    if (isCase && isSingle) kind = Number(position) === 1 ? 'single' : 'case';
    else if (isCase) kind = 'case';
    else if (isSingle) kind = 'single';
    const count = parseInt(text.split(' ')[0], 10);
    return {kind, count: kind === 'case' && count > 0 ? count : 1};
  }
  vh.variantKind = variantKind;

  async function mergeSixPacks(cart) {
    const caseSize = Number(config.caseSize) || 6;
    let merged = false;

    for (const item of cart.items || []) {
      if (item.quantity < caseSize || isServiceItem(item)) continue;
      if (String(item.product_type || '').toLowerCase() === 'abonnement') continue;
      if (item.selling_plan_allocation) continue;

      const product = await fetchProduct(item.handle);
      if (!product || !Array.isArray(product.variants)) continue;
      const lineIndex = product.variants.findIndex((v) => Number(v.id) === Number(item.variant_id));
      if (lineIndex < 0 || variantKind(product.variants[lineIndex].title, lineIndex + 1).kind !== 'single') continue;
      const caseVariant = product.variants.find((v, index) => {
        const kind = variantKind(v.title, index + 1);
        return v.available && kind.kind === 'case' && kind.count === 1;
      });
      if (!caseVariant) continue;

      const cases = Math.floor(item.quantity / caseSize);
      const rest = item.quantity - cases * caseSize;
      const existingCase = (cart.items || []).find((line) => Number(line.variant_id) === Number(caseVariant.id) && !line.selling_plan_allocation);

      const updates = {};
      updates[item.key] = rest;
      if (existingCase) updates[existingCase.key] = existingCase.quantity + cases;
      await api.update(updates);
      // The case keeps the bottles' line properties, e.g. Rebuy's attribution when the wine came from a recommendation
      const properties = item.properties && typeof item.properties === 'object' && Object.keys(item.properties).length ? item.properties : null;
      if (!existingCase) await api.add([properties ? {id: caseVariant.id, quantity: cases, properties} : {id: caseVariant.id, quantity: cases}]);

      const saving = item.price * cases * caseSize - caseVariant.price * cases;
      const message = (config.strings && config.strings.sixpackMerged) || '';
      toast(message.replace('{{ amount }}', saving > 0 ? vh.formatMoney(saving) : '').replace('{{ count }}', cases));
      merged = true;
    }
    return merged;
  }

  document.addEventListener('theme:cart:change', () => {
    feeChecked = false;
    // Right after an add, Shopify's own render of the cart is at hand: check it at once, without a request
    const cart = freshCart;
    scheduleSync(cart ? {cart, delay: 0} : {});
  });
  document.addEventListener('DOMContentLoaded', () => {
    /* Skip the start-up check for empty carts (saves a request on most page views). */
    if (window.theme && Number(window.theme.subtotal) === 0) return;
    scheduleSync({silent: true});
  });

  /* Make sure the fee is in the cart right before checkout (fast buyers). When the cart has settled and the fee
     was checked after the last change, checkout goes straight on. */
  // "Til betaling" shows that it is working from the tap until checkout opens (F-144). Never disabled: a disabled
  // submitter leaves "checkout" out of the form, and Shopify would stay on /cart
  function checkoutBusy(button, on) {
    if (!button) return;
    button.classList.toggle('is-loading', on);
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    clearTimeout(button._vhBusyTimer);
    if (on) button._vhBusyTimer = setTimeout(() => checkoutBusy(button, false), 12000);
  }
  // The MitID check (snippets/mitid-age-verification.liquid) takes the click on "Til betaling" before the form is sent,
  // for every visitor who isn't verified, and goes to checkout itself: it shows and ends the sign through this
  vh.checkoutBusy = checkoutBusy;
  // Back from checkout (the page restored from the browser's cache): the button is itself again
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('button[name="checkout"].is-loading').forEach((button) => checkoutBusy(button, false));
    document.querySelectorAll('form#cartForm[data-vh-holding]').forEach((form) => delete form.dataset.vhHolding);
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cartForm') return;
      const submitter = event.submitter;
      const toCheckout = Boolean(submitter && submitter.name === 'checkout');
      if (toCheckout) checkoutBusy(submitter, true);
      if (form.dataset.vhChecked === 'true' || !config.feeEnabled || !config.feeVariantId) return;
      if (!toCheckout) return;
      // A second tap while the first one waits: the first one goes on to checkout
      if (form.dataset.vhHolding === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (isSettled() && feeChecked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      form.dataset.vhHolding = 'true';
      try {
        await vh.cartSettled();
        if (!feeChecked) await ensureFee(await api.cart());
      } catch (e) {
        /* never block checkout */
      }
      delete form.dataset.vhHolding;
      form.dataset.vhChecked = 'true';
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      } else {
        form.submit();
      }
    },
    true
  );

  /* ------------------------------------------------------------------------
     3 + 4. Add-ons (Express) and Udskudte betalinger (single choice)
     ------------------------------------------------------------------------ */
  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-vh-addon], [data-vh-deferred]');
    if (!input) return;
    const variantId = Number(input.dataset.variantId);
    const isDeferred = input.hasAttribute('data-vh-deferred');
    const holder = input.closest('label');
    const list = isDeferred ? input.closest('[data-vh-deferred-list]') : null;
    // What the customer chose at the click. The tick itself can be changed by a cart sync while Shopify answers.
    const wanted = input.checked;
    holder && holder.classList.add('is-loading');

    // Betal senere is one choice: the other dates are unticked at once, and the list waits for the cart
    // (a second click mid-update could otherwise leave two dates in the cart)
    if (list) {
      if (wanted) {
        list.querySelectorAll('[data-vh-deferred]').forEach((el) => {
          if (el !== input) el.checked = false;
        });
      }
      list.classList.add('is-busy');
      markFooterOptions();
    }

    try {
      // In the queue: checkout waits until the choice is in the cart (a click on "Til betaling" right after the tick
      // lost it, F-065), and a "+" or "Fjern" pressed meanwhile is sent after it
      await queueWrite(async () => {
        const cart = await api.cart();
        const updates = {};
        if (isDeferred) {
          const list = input.closest('[data-vh-deferred-list]');
          const otherIds = Array.from(list ? list.querySelectorAll('[data-vh-deferred]') : [])
            .map((el) => Number(el.dataset.variantId))
            .filter((id) => id !== variantId);
          cart.items.forEach((item) => {
            if (otherIds.includes(Number(item.variant_id))) updates[item.key] = 0;
          });
          rememberDetails(true);
        }
        const existing = cart.items.find((item) => Number(item.variant_id) === variantId);
        if (wanted) {
          if (Object.keys(updates).length) await api.update(updates);
          if (!existing) await api.add([{id: variantId, quantity: 1}]);
          else if (existing.quantity !== 1) await api.update({[existing.key]: 1});
        } else if (existing) {
          updates[existing.key] = 0;
          await api.update(updates);
        } else if (Object.keys(updates).length) {
          await api.update(updates);
        }
      });
    } catch (error) {
      console.warn('[vh-cart] add-on update failed', error);
    } finally {
      holder && holder.classList.remove('is-loading');
      // The ticks follow the cart before the list is unlocked, so a click right after the unlock starts from the
      // real state. A second date chosen 0.3 s after the first could otherwise leave the cart without one (F-041).
      await syncFooterInputs({force: true});
      list && list.classList.remove('is-busy');
      refresh({silent: true});
    }
  });

  /* Drawer footer options row (Express / Betal senere / Gave): one menu open at a time, closes on outside
     click or Escape, marks options that are in use, and follows the cart after every change. */
  function markFooterOptions() {
    document.querySelectorAll('[data-vh-fopt-menu]').forEach((menu) => {
      const textarea = menu.querySelector('textarea');
      const checked = menu.querySelector('input:checked');
      const inUse = Boolean(checked) || Boolean(textarea && textarea.value.trim());
      menu.classList.toggle('is-set', inUse);
      const state = menu.querySelector('[data-vh-fopt-state]');
      if (!state) return;
      const option = checked && checked.closest('label');
      const chosen = option && option.querySelector('.vh-deferred__title');
      const chosenPrice = option && option.querySelector('.vh-deferred__price');
      if (chosen) state.textContent = chosen.textContent.trim() + (chosenPrice ? ` · ${chosenPrice.textContent.trim()}` : '');
      else if (inUse && state.dataset.set) state.textContent = state.dataset.set;
      else state.textContent = state.dataset.empty || '';
    });
  }
  function closeFooterMenus(except) {
    document.querySelectorAll('[data-vh-fopt-menu][open]').forEach((menu) => {
      if (menu !== except) menu.open = false;
    });
  }
  document.addEventListener(
    'toggle',
    (event) => {
      if (event.target.matches && event.target.matches('[data-vh-fopt-menu]') && event.target.open) closeFooterMenus(event.target);
    },
    true
  );
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-vh-fopt-menu]')) closeFooterMenus();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeFooterMenus();
  });
  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-vh-fopts]')) markFooterOptions();
  });
  document.addEventListener('input', (event) => {
    if (event.target.closest('[data-vh-fopts]')) markFooterOptions();
  });

  /* Info buttons by Express and Betal senere (snippets/vh-fopt-info.liquid; docs/QA.md, F-113): one explanation open
     at a time, above its row with the arrow at the button. A tap outside, the close button or Escape closes it; the
     close button and Escape give the focus back to the info button, and Escape leaves the drawer open. */
  let infoEscape = false;
  function closeInfo({focus = false} = {}) {
    document.querySelectorAll('[data-vh-info][aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const pop = document.getElementById(button.getAttribute('aria-controls'));
      if (pop) pop.hidden = true;
      if (focus) button.focus();
    });
  }
  function openInfo(button) {
    const pop = document.getElementById(button.getAttribute('aria-controls'));
    if (!pop) return;
    closeInfo();
    closeFooterMenus();
    pop.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const cell = pop.parentElement.getBoundingClientRect();
    const box = button.getBoundingClientRect();
    pop.style.setProperty('--vh-info-arrow', `${Math.round(box.left + box.width / 2 - cell.left)}px`);
  }
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target.closest) return;
    const button = target.closest('[data-vh-info]');
    if (button) {
      if (button.getAttribute('aria-expanded') === 'true') closeInfo();
      else openInfo(button);
    } else if (target.closest('[data-vh-info-close]')) {
      closeInfo({focus: true});
    } else if (!target.closest('[data-vh-info-pop]')) {
      closeInfo();
    }
  });
  // Broadcast closes the drawer on Escape's keyup: while an explanation is open, Escape only closes that
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' || !document.querySelector('[data-vh-info][aria-expanded="true"]')) return;
      infoEscape = true;
      event.stopPropagation();
      closeInfo({focus: true});
    },
    true
  );
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || !infoEscape) return;
      infoEscape = false;
      event.stopPropagation();
    },
    true
  );
  document.addEventListener('theme:cart-drawer:close', () => closeInfo());
  // Ticks follow the cart itself (also after a silent refresh, which sends no cart event)
  async function syncFooterInputs({force = false} = {}) {
    const inputs = document.querySelectorAll('[data-vh-fopts] [data-vh-addon], [data-vh-fopts] [data-vh-deferred]');
    if (!inputs.length) return;
    const known = freshCart;
    try {
      const cart = known || (await api.cart());
      const ids = new Set(cart.items.map((item) => Number(item.variant_id)));
      inputs.forEach((input) => {
        // a list that is waiting for its own change keeps the customer's choice until that change has finished
        if (!force && input.closest('[data-vh-deferred-list].is-busy')) return;
        input.checked = ids.has(Number(input.dataset.variantId));
      });
      markFooterOptions();
    } catch (e) {}
  }
  document.addEventListener('theme:cart:change', () => syncFooterInputs());

  /* ABConvert price tests, such as "Express håndtering" at 20 kr or 39,95 kr. ABConvert sets the price in the cart and
     at checkout (cart transform), and rewrites prices in product cards it recognises. The add-on prices are the
     theme's own markup, so they follow ABConvert's assignment and price table here, after every render. */
  function applyTestPrices() {
    const table = window.abconvertPriceCartTransformTestDict;
    if (!table) return;
    const groups = {};
    try {
      const stored = JSON.parse(localStorage.getItem('abconvert.price.v2') || 'null');
      if (stored && stored.status === 'active') {
        (stored.assignments || []).forEach((assignment) => {
          (assignment.productIds || []).forEach((id) => {
            groups[String(id)] = assignment.groupIndex;
          });
        });
      }
    } catch (e) {}
    document.querySelectorAll('[data-vh-ab-variant]').forEach((el) => {
      const productId = el.dataset.vhAbProduct;
      const test = table[productId];
      if (!test || groups[productId] === undefined) return;
      const byGroup = test.variantToGroupIndexToPrice && test.variantToGroupIndexToPrice[el.dataset.vhAbVariant];
      const price = byGroup && byGroup[groups[productId]] ? parseFloat(byGroup[groups[productId]].price) : NaN;
      if (!Number.isFinite(price)) return;
      let text = vh.formatMoney(Math.round(price * 100));
      if ('vhAbTrim' in el.dataset) text = text.replace(/,00(?!\d)/, '');
      if (el.textContent !== text) el.textContent = text;
    });
  }
  if (window.abconvertPriceCartTransformTestDict) {
    applyTestPrices();
    new MutationObserver((records) => {
      const added = records.some((record) =>
        Array.from(record.addedNodes).some((node) => node.nodeType === 1 && (node.matches('[data-vh-ab-variant]') || node.querySelector('[data-vh-ab-variant]')))
      );
      if (added) applyTestPrices();
    }).observe(document.body, { childList: true, subtree: true });
    // A new visitor is assigned by ABConvert's own script, a moment after this one runs
    [800, 2500, 6000].forEach((ms) => window.setTimeout(applyTestPrices, ms));
  }
  // …and when the drawer opens (the page may have been rendered while a change was still on its way)
  document.addEventListener('theme:cart-drawer:open', (event) => {
    if (pendingAdds) return; // opened for an add: the cart change it brings updates the ticks
    if (event.detail && event.detail.target) syncFooterInputs();
  });
  markFooterOptions();

  /* 5. Keep the Løn accordion open across re-renders while the customer uses it */
  const DETAILS_KEY = 'vh:deferred-open';
  function rememberDetails(open) {
    try {
      sessionStorage.setItem(DETAILS_KEY, open ? '1' : '0');
    } catch (e) {}
  }
  document.addEventListener(
    'toggle',
    (event) => {
      if (event.target.matches && event.target.matches('[data-vh-deferred-details]')) {
        rememberDetails(event.target.open);
      }
    },
    true
  );
  document.addEventListener('theme:cart:change', () => {
    let open = false;
    try {
      open = sessionStorage.getItem(DETAILS_KEY) === '1';
    } catch (e) {}
    if (open) document.querySelectorAll('[data-vh-deferred-details]').forEach((el) => (el.open = true));
  });

  /* ------------------------------------------------------------------------
     6. Post-add-to-cart offers (cart drawer blocks "Vinhuset: Tilbud efter køb")
     ------------------------------------------------------------------------ */
  const OFFER_PREFIX = 'vh:offer:';

  function offerSeen(id, frequency) {
    try {
      if (frequency === 'session') return sessionStorage.getItem(OFFER_PREFIX + id) === '1';
      if (frequency === 'day') {
        const last = Number(localStorage.getItem(OFFER_PREFIX + id) || 0);
        return Date.now() - last < 24 * 60 * 60 * 1000;
      }
    } catch (e) {}
    return false;
  }

  function markOfferSeen(id, frequency) {
    try {
      if (frequency === 'session') sessionStorage.setItem(OFFER_PREFIX + id, '1');
      if (frequency === 'day') localStorage.setItem(OFFER_PREFIX + id, String(Date.now()));
    } catch (e) {}
  }

  function parseEnd(value) {
    if (!value) return null;
    if (window.vh && typeof window.vh.parseCphDate === 'function') return window.vh.parseCphDate(value);
    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/);
    if (!match) return null;
    const [, y, m, d, hh = '23', mm = '59'] = match;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm));
  }

  async function offerMatches(tpl, added, cart) {
    const d = tpl.dataset;
    const end = parseEnd(d.end);
    if (end && Date.now() > end.getTime()) return false;
    if (offerSeen(d.offerId, d.frequency)) return false;

    const offerVariant = Number(d.offerVariant);
    if ((cart.items || []).some((item) => Number(item.variant_id) === offerVariant || item.handle === d.offerProduct)) return false;
    if (added.some((item) => item.handle === d.offerProduct)) return false;

    const subtotal = Number(cart.total_price) || 0;
    if (Number(d.min) > 0 && subtotal < Number(d.min)) return false;
    if (Number(d.max) > 0 && subtotal > Number(d.max)) return false;

    const values = String(d.triggerValue || '')
      .split(',')
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);

    let triggered = false;
    switch (d.trigger) {
      case 'product_type':
        triggered = added.some((item) => values.includes(String(item.product_type || '').toLowerCase()));
        break;
      case 'products': {
        const handles = String(d.triggerHandles || '').split(',').filter(Boolean);
        triggered = added.some((item) => handles.includes(item.handle));
        break;
      }
      case 'tag': {
        for (const item of added) {
          const product = await fetchProduct(item.handle);
          const tags = (product && product.tags ? product.tags : []).map((t) => String(t).toLowerCase());
          if (tags.some((t) => values.includes(t))) {
            triggered = true;
            break;
          }
        }
        break;
      }
      default:
        triggered = true;
    }
    if (!triggered) return false;

    const chance = Number(d.chance) || 100;
    return Math.random() * 100 < chance;
  }

  function startOfferTimer(card, end) {
    const el = card.querySelector('[data-vh-offer-timer]');
    if (!el || !end) return;
    el.hidden = false;
    const strings = config.strings || {};
    const label = strings.offerEnds || 'Ends in';
    // "3 dage": the day count in the store's language (vinhuset.time.days_one / days_other)
    const daysText = (n) => String((n === 1 ? strings.daysOne : strings.daysOther) || '{{ count }} days').replace('{{ count }}', n);
    const tick = () => {
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        card.remove();
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      el.textContent = h >= 48 ? `${label} ${daysText(Math.floor(h / 24))}` : `${label} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
      card._timer = setTimeout(tick, 1000);
    };
    tick();
  }

  function showOffer(tpl) {
    const slot = document.querySelector('[data-vh-offer-slot]');
    if (!slot) return;
    slot.innerHTML = '';
    slot.appendChild(tpl.content.cloneNode(true));
    const card = slot.querySelector('[data-vh-offer-card]');
    if (!card) return;
    card.classList.add('is-entering');
    requestAnimationFrame(() => card.classList.remove('is-entering'));
    markOfferSeen(tpl.dataset.offerId, tpl.dataset.frequency);
    startOfferTimer(card, parseEnd(tpl.dataset.end));
    document.dispatchEvent(new CustomEvent('vh:offer:shown', {detail: {id: tpl.dataset.offerId}}));
  }

  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-vh-offer-close]');
    if (!close) return;
    const card = close.closest('[data-vh-offer-card]');
    if (card) {
      clearTimeout(card._timer);
      card.remove();
    }
  });

  document.addEventListener('theme:product:add', async (event) => {
    const known = freshCart;
    const detail = event.detail || {};
    const button = detail.button;
    const acceptedCard = button && button.closest ? button.closest('[data-vh-offer-card]') : null;
    if (acceptedCard) {
      clearTimeout(acceptedCard._timer);
      acceptedCard.classList.add('is-accepted');
      toast(config.strings && config.strings.offerAdded);
      setTimeout(() => acceptedCard.remove(), 600);
      return;
    }

    const templates = Array.from(document.querySelectorAll('[data-vh-offer-template]'));
    if (!templates.length || !detail.response) return;
    const response = detail.response;
    const added = (Array.isArray(response.items) ? response.items : [response]).filter((item) => item && item.variant_id && !isServiceItem(item));
    if (!added.length) return;

    try {
      const cart = known || (await api.cart());
      for (const tpl of templates) {
        if (await offerMatches(tpl, added, cart)) {
          showOffer(tpl);
          break;
        }
      }
    } catch (e) {
      /* offers are optional */
    }
  });

  /* ------------------------------------------------------------------------
     7. Faster "Læg i kurv" (Broadcast's CartItems.addToCart, same events and error handling)
     - Optimistic UI: the drawer opens at the tap with the product on its way in (image, name, variant,
       price), so the customer sees it in the cart at once instead of waiting for Shopify.
     - Bundled section rendering: the add request asks for the drawer as well (?sections=api-cart-items),
       so an add is one request instead of an add followed by a cart fetch.
     - Shopify stays the source of truth: its render replaces the line as soon as it answers, an error takes
       the line out again and shows Shopify's message as before, and checkout waits until the cart has
       settled (vh.cartSettled, also used by the MitID check).
     ------------------------------------------------------------------------ */
  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);
  }

  function selectedVariant(scope) {
    const el = scope && scope.querySelector('[data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function firstImage(scope, variant) {
    const featured = variant && variant.featured_image && variant.featured_image.src;
    if (featured) return `${featured}${featured.includes('?') ? '&' : '?'}width=180`;
    const images = scope ? scope.querySelectorAll('.product__media img, product-images img, .vh-card__media img, .vh-upsell__media img, img') : [];
    for (const img of images) {
      const src = img.currentSrc || img.src;
      if (src && !src.startsWith('data:')) return src;
    }
    return '';
  }

  function readJson(text) {
    try {
      return text ? JSON.parse(text) : null;
    } catch (e) {
      return null;
    }
  }

  /* What the add source says about the line Shopify will render (snippets/vh-line-data.liquid): the variant field's
     data-vh-line (product card, cart recommendation, offer), else the product page's list of its variants, with the
     subscription's name when one is chosen */
  function lineData(form, scope, formData) {
    const field = form && form.elements ? form.elements.namedItem('id') : null;
    const el = field && field.tagName === 'SELECT' ? field.selectedOptions[0] : field && field.tagName ? field : null;
    const own = el ? readJson(el.getAttribute('data-vh-line')) : null;
    if (own) return own;
    const list = (form && form.querySelector('[data-vh-line-variants]')) || scope.querySelector('[data-vh-line-variants]');
    const all = list ? readJson(list.textContent) : null;
    const data = all && all.variants ? all.variants[String(formData.get('id'))] : null;
    if (!data) return null;
    const plan = formData.get('selling_plan');
    return plan && all.plans && all.plans[plan] ? Object.assign({}, data, {plan: all.plans[plan]}) : data;
  }

  /* The line the customer sees while Shopify adds the product: the rows of Shopify's cart line
     (snippets/cart-line-items.liquid) filled from the add source, so nothing moves when Shopify's line takes its place
     (docs/QA.md, F-111). The quantity row keeps its height with the buttons hidden, and "Lægges i kurven" stands where
     they come. */
  function pendingLine(formData, button) {
    const form = button && (button.form || button.closest('form'));
    // The product around the button: product card, offer or cart recommendation (also inside a product page), else the product page
    const holder = (el) => el && (el.closest('grid-item') || el.closest('[data-vh-offer-card]') || el.closest('[data-quick-add-holder]') || el.closest('product-info'));
    const scope = holder(form) || holder(button);
    if (!scope) return null;
    const titleEl = scope.querySelector('.product__title, .vh-card__title, .vh-upsell__title, .vh-offer__title');
    let title = titleEl ? titleEl.textContent.trim() : '';
    if (!title && button) {
      // The add buttons on cards are labelled "Tilføj til kurv: <product>"
      const label = button.getAttribute('data-label-base') || button.getAttribute('aria-label') || '';
      title = label.includes(':') ? label.slice(label.indexOf(':') + 1).trim() : '';
    }
    if (!title) return null;

    const data = lineData(form, scope, formData);
    const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    const variant = selectedVariant(scope);
    const variantMatches = variant && String(variant.id) === String(formData.get('id'));
    let variantLabel = data ? data.variant : '';
    if (!data) {
      // An add button without the data: the variant and price from the page
      const idField = form && form.elements ? form.elements.namedItem('id') : null;
      const variantEl = scope.querySelector('.vh-upsell__variant');
      if (idField && idField.tagName === 'SELECT' && idField.selectedOptions[0]) variantLabel = idField.selectedOptions[0].getAttribute('data-title') || idField.selectedOptions[0].textContent.trim();
      else if (variantMatches && variant.title && variant.title !== 'Default Title') variantLabel = variant.title;
      else if (variantEl) variantLabel = variantEl.textContent.trim();
    }
    const price = data ? Number(data.price) : variantMatches ? Number(variant.price) : NaN;
    const compare = (data ? Number(data.compare) : variantMatches ? Number(variant.compare_at_price) : 0) || 0;
    const money = (cents) => (typeof vh.formatMoney === 'function' ? vh.formatMoney(cents) : '');
    const strings = config.strings || {};
    const free = (window.theme && window.theme.strings && window.theme.strings.free) || '';
    const total = Number.isFinite(price) ? (price === 0 ? free : money(price * quantity)) : '';
    const saving = Number.isFinite(price) && compare > price ? (compare - price) * quantity : 0;
    const bottles = (data && data.bottles) || '';
    const image = firstImage(scope, variantMatches ? variant : null);
    const note = strings.adding || '';

    let meta = '';
    if (variantLabel) meta = `<p class="cart__item__selected-options vh-line__variant"><span>${escapeHtml(variantLabel)}</span>${bottles ? `<span class="vh-line__bottles">· ${escapeHtml(bottles)}</span>` : ''}</p>`;
    else if (bottles) meta = `<p class="cart__item__selected-options vh-line__variant"><span>${escapeHtml(bottles)}</span></p>`;
    if (data && data.plan) meta += `<p class="cart__item__selling-plan"><span>${escapeHtml(data.plan)}</span></p>`;

    const line = document.createElement('div');
    line.className = 'cart__item cart__item--no-border vh-line--pending';
    line.setAttribute('data-vh-pending', '');
    line.setAttribute('aria-busy', 'true');
    line.innerHTML = `
      <div class="cart__item__image${image ? '' : ' image--empty'}"><span class="vh-line__img">${image ? `<img src="${escapeHtml(image)}" alt="" width="90" height="90" decoding="async">` : ''}</span></div>
      <div class="cart__item__content">
        <div class="cart__item__content__left">
          <div class="cart__item__content-inner">
            <h4 class="cart__item__title"><span>${escapeHtml(title)}</span></h4>
            <div class="cart__item__meta body-x-small">${meta}</div>
          </div>
          <div class="cart__quantity-wrapper${data && data.qty === false ? ' vh-line__qty--hidden' : ''}">
            <div class="cart__quantity-counter">
              <div class="cart__quantity" aria-hidden="true"><input class="cart__quantity-field" type="number" value="${quantity}" tabindex="-1" readonly></div>
              <span class="cart__item__remove" aria-hidden="true">${escapeHtml(strings.remove || '')}</span>
              ${note ? `<p class="vh-line__pending-note" role="status">${escapeHtml(note)}</p>` : ''}
            </div>
          </div>
        </div>
        <div class="cart__item__content__right">
          <div class="cart__item__price">${escapeHtml(total)}</div>
          ${saving > 0 ? `<s class="vh-line__compare">${escapeHtml(money(compare * quantity))}</s><div class="vh-line__saving">${escapeHtml(strings.youSave || '')} <span class="vh-nowrap">${escapeHtml(money(saving))}</span></div>` : ''}
        </div>
      </div>`;
    return line;
  }

  /* A product already in the cart: Shopify adds to its line where it is (the same variant, subscription and line
     properties), so that line waits instead of a new line on top that goes again (F-111). Read from the cart this page
     last saw; when that is unsure, a new line waits on top as before. */
  function lineInCart(items, formData) {
    const cart = vh.lastCart;
    if (!cart || !Array.isArray(cart.items) || !items.itemsHolder) return null;
    const id = String(formData.get('id'));
    const plan = Boolean(formData.get('selling_plan'));
    const props = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') props[match[1]] = String(value);
    });
    const sameProps = (have) => {
      const keys = Object.keys(have || {}).filter((key) => have[key] !== '' && have[key] != null);
      return keys.length === Object.keys(props).length && keys.every((key) => String(have[key]) === props[key]);
    };
    const matches = cart.items.filter((item) => String(item.variant_id) === id && Boolean(item.selling_plan_allocation) === plan && sameProps(item.properties));
    if (matches.length !== 1) return null;
    return Array.from(items.itemsHolder.querySelectorAll('.cart__item[data-item]')).find((el) => el.getAttribute('data-item') === matches[0].key) || null;
  }

  /* That line while Shopify answers: the new quantity, dimmed as when "+" is pressed, and scrolled into view. Returns
     the way back (for an add that fails) */
  function waitOnLine(line, quantityAdded) {
    const field = line.querySelector('.cart__quantity-field');
    const before = field ? field.value : null;
    if (field) field.value = String((parseInt(before, 10) || 0) + quantityAdded);
    line.classList.add('is-loading');
    line.setAttribute('aria-busy', 'true');
    const note = (config.strings && config.strings.adding) || '';
    const status = note ? document.createElement('p') : null;
    if (status) {
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.textContent = note;
      line.appendChild(status);
    }
    requestAnimationFrame(() => {
      let scroller = line.parentElement;
      while (scroller && scroller !== document.body && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) scroller = scroller.parentElement;
      if (!scroller || scroller === document.body) return;
      const box = line.getBoundingClientRect();
      const view = scroller.getBoundingClientRect();
      if (box.top < view.top || box.bottom > view.bottom) scroller.scrollTop += box.top - view.top - 16;
    });
    return () => {
      if (field && before !== null) field.value = before;
      line.classList.remove('is-loading');
      line.removeAttribute('aria-busy');
      if (status) status.remove();
    };
  }

  /* Shopify's message in the drawer's own error box (heading "Beklager, vi har ikke nok af dette produkt på lager.") */
  let stockNotice = null;
  function showDrawerError(items, message) {
    const holder = items.cartErrorHolder || document.querySelector('cart-drawer [data-cart-errors]');
    if (!holder || !message) return;
    const text = holder.querySelector('[data-error-message]');
    if (text) text.textContent = message;
    holder.classList.add('is-expanded');
  }

  function fastAddToCart(items, formData, button) {
    const drawer = items.cartDrawer;
    const wasEmpty = drawer.classList.contains('is-empty');
    const wasOpen = drawer.classList.contains('is-open');
    let line = null;
    let cartLine = null;
    // Lets other scripts start on the new cart at the tap, e.g. Rebuy's recommendations (assets/vh-rebuy.js)
    document.dispatchEvent(new CustomEvent('vh:cart:adding', {detail: {formData, button}}));
    try {
      cartLine = lineInCart(items, formData);
      if (!cartLine) line = pendingLine(formData, button);
    } catch (e) {
      line = null;
      cartLine = null;
    }
    const shown = line || cartLine;

    pendingAdds += 1;
    feeChecked = false;
    items.cart.classList.add('is-loading');
    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }
    const quickAddHolder = button && button.closest('[data-quick-add-holder]');
    if (quickAddHolder) quickAddHolder.classList.add('is-visible');

    // Show it now: the product on top of the cart (or its own line when it is there already), the count up, and the drawer open
    const counts = shown ? Array.from(document.querySelectorAll('cart-count')).filter((el) => typeof el.update === 'function') : [];
    const countsBefore = counts.map((el) => Number(el.getAttribute('data-cart-count')) || 0);
    const quantityAdded = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    counts.forEach((el, index) => {
      el.cartCount = countsBefore[index] + quantityAdded;
      el.update();
    });
    const release = cartLine ? waitOnLine(cartLine, quantityAdded) : null;
    if (shown) {
      if (line) items.itemsHolder.prepend(line);
      drawer.classList.remove('is-empty');
      drawer.classList.add('vh-cart-adding');
      if (!wasOpen && typeof drawer.openCartDrawer === 'function') {
        // Slide in without the staggered fade of the blocks, so the product is readable at once
        drawer.classList.add('is-updated');
        drawer.openCartDrawer();
      }
    }

    const undo = () => {
      counts.forEach((el, index) => {
        el.cartCount = countsBefore[index];
        el.update();
      });
      if (line) line.remove();
      if (release) release();
      drawer.classList.remove('vh-cart-adding');
      if (wasEmpty && !drawer.querySelector('[data-item]')) drawer.classList.add('is-empty');
      // The error is shown by the product, as before: take the drawer away again if this add opened it
      if (shown && !wasOpen && typeof drawer.closeCartDrawer === 'function') drawer.closeCartDrawer();
    };

    formData.append('sections', DRAWER_SECTION);
    formData.append('sections_url', window.location.pathname);

    // In the queue: an add sent while another change is on its way could lose one of them (F-146)
    return queueWrite(() =>
      fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: {'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript'},
        body: formData,
      }).then((response) => response.json())
    )
      .then((response) => {
        if (response.status) {
          // Shopify said no (sold out, limit reached …) – but it may still have added what is in stock ("Der blev
          // kun føjet 6 varer …"). So Shopify's cart decides what is shown: its message by the product and in the
          // drawer, the lines and the count as they are, and the fee check on that cart (docs/QA.md, F-034).
          const message = response.description || response.message || '';
          items.showCannotAddMoreInCart = false;
          items.addToCartError(response, button);
          if (button) {
            button.classList.remove('is-loading');
            button.disabled = false;
          }
          return fetch(`${routes.cart_url || '/cart'}?section_id=${DRAWER_SECTION}`, {headers: {'X-Requested-With': 'XMLHttpRequest'}})
            .then((res) => res.text())
            .then((html) => {
              if (line) line.remove();
              if (release) release();
              drawer.classList.remove('vh-cart-adding');
              if (!buildDrawer(html, {silent: true})) {
                undo();
                return;
              }
              const id = String(formData.get('id'));
              const inCart = Boolean(vh.lastCart && (vh.lastCart.items || []).some((item) => String(item.variant_id) === id));
              if (inCart) {
                // Part of it went in: keep the cart open with Shopify's message, also after the fee check rebuilds it
                stockNotice = {message, until: Date.now() + 10000};
                showDrawerError(items, message);
              } else if (shown && !wasOpen && typeof drawer.closeCartDrawer === 'function') {
                drawer.closeCartDrawer();
              }
            })
            .catch(() => undo());
        }

        const html = response.sections && response.sections[DRAWER_SECTION];
        let content = null;
        if (html) {
          const holder = document.createElement('div');
          holder.innerHTML = html;
          content = holder.querySelector('[data-api-content]');
        }
        // The drawer is already open: Shopify's lines take the pending line's place without fading in
        if (content && drawer.classList.contains('is-open')) {
          content.querySelectorAll('[data-animation]').forEach((el) => el.classList.add('is-animated'));
        }

        freshCart = readCartJson(content);
        if (freshCart) vh.lastCart = freshCart;
        try {
          if (button) {
            button.classList.remove('is-loading');
            button.classList.add('is-added');
            button.dispatchEvent(new CustomEvent('theme:product:add', {detail: {response, button}, bubbles: true}));
          }
          drawer.classList.remove('vh-cart-adding');
          if (content) items.build(content);
          else {
            if (line) line.remove();
            if (release) release();
            items.getCart();
          }
        } finally {
          freshCart = null;
        }

        if (typeof window.publish === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
          window.publish(window.theme.PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id'), cartData: response});
        }
      })
      .catch((error) => {
        undo();
        items.addToCartError(error, button);
        items.enableCartButtons();
      })
      .finally(() => {
        pendingAdds -= 1;
        checkSettled();
      });
  }

  function enhanceAddToCart() {
    const items = drawerItems();
    if (!items || items.vhFastAdd || typeof items.addToCart !== 'function' || !items.cartDrawer) return;
    if (!window.theme || !window.theme.settings || window.theme.settings.cartType !== 'drawer') return;
    const original = items.addToCart;
    items.vhFastAdd = true;
    // Broadcast folds the drawer's error box away after every rebuild; a stock message stays until it is closed
    const toggleError = items.toggleErrorMessage;
    if (typeof toggleError === 'function') {
      items.toggleErrorMessage = function () {
        toggleError.call(items);
        if (stockNotice && Date.now() < stockNotice.until) showDrawerError(items, stockNotice.message);
      };
    }
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-error-close]')) stockNotice = null;
    });
    // CartItems.cartAddEvent calls this.addToCart, so the instance method is the one to replace
    items.addToCart = function (formData, button) {
      if (!window.fetch || !formData || typeof formData.append !== 'function') return original.call(items, formData, button);
      return fastAddToCart(items, formData, button);
    };
  }
  enhanceAddToCart();
  document.addEventListener('DOMContentLoaded', enhanceAddToCart);
  document.addEventListener('shopify:section:load', enhanceAddToCart);

  /* ------------------------------------------------------------------------
     8. Quantity changes and "Fjern" go to the line's key (F-064)
     Broadcast's CartItems.updateCart names the line by its place in the cart (data-item-index). Express, Løn and the
     fee are added at the top of the cart, so each of them moves the products a place down (or up, when it is taken
     out) until the cart is rendered again, 1.5–4 s later. A "+" or "Fjern" in that moment changed or removed the
     product next to the one the customer pressed. The key (data-item, the button's data-id) belongs to the line
     itself, so the change reaches the right line wherever it is, as Smart Cart does on the live theme. The change
     also waits its turn behind an Express/Løn change or gift note save still on its way (queueWrite). Everything else
     is Broadcast's method (assets/theme.dev.js, updateCart): the same request, loading states, errors and render.
     ------------------------------------------------------------------------ */
  function updateCartByKey(updateData = {}, currentItem = null) {
    this.cart.classList.add('is-loading');

    const quantity = updateData.quantity;
    if (currentItem !== null) currentItem.classList.add(quantity ? 'is-loading' : 'is-removed');
    this.disableCartButtons();

    const item = this.cart.querySelector(`[data-item="${updateData.id}"]`) || currentItem;
    const key = updateData.id || (item && item.getAttribute('data-item'));
    const title = item && item.hasAttribute('data-item-title') ? item.getAttribute('data-item-title') : null;

    if (!key) return;

    queueWrite(() =>
      fetch(routes.cart_change_url || '/cart/change', {
        method: 'post',
        headers: JSON_HEADERS,
        body: JSON.stringify({id: String(key), quantity}),
      }).then((response) => response.text())
    )
      .then((state) => {
        if (JSON.parse(state).errors) {
          this.cartUpdateFailed = true;
          this.updateErrorText(title);
          this.toggleErrorMessage();
          this.resetLineItem(currentItem);
          this.enableCartButtons();
          this.bindDiscountEventListeners();
          return;
        }
        this.getCart();
      })
      .catch((error) => {
        console.log(error);
        this.enableCartButtons();
      });
  }

  // The drawer and the cart page are both <cart-items>. Broadcast binds the method to each element when it connects
  // (this.updateCart = this.updateCart.bind(this)), so the elements already on the page get it here, and elements
  // connected later bind it from the class.
  if (window.customElements) {
    customElements.whenDefined('cart-items').then(() => {
      const CartItems = customElements.get('cart-items');
      if (!CartItems || typeof CartItems.prototype.updateCart !== 'function') return;
      CartItems.prototype.updateCart = updateCartByKey;
      document.querySelectorAll('cart-items').forEach((items) => {
        items.updateCart = updateCartByKey.bind(items);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Cart attributes typed on /cart or in the drawer (the gift note) are saved on the cart while the customer types
     and when the field is left. Checkout does not always submit the cart form (the MitID check and the fee check go
     to checkout themselves), so the form alone would lose them. Checkout waits for a save on its way, and a save
     still waiting for the pause in typing is sent at once (vh.cartSettled, F-065). keepalive lets a save finish
     during navigation all the same.
     ------------------------------------------------------------------------ */
  let attributeTimer = null;
  function saveAttribute(field, delay) {
    const match = /^attributes\[(.+)\]$/.exec(field.name || '');
    if (!match || field.type === 'hidden' || field.type === 'checkbox' || field.type === 'radio') return;
    clearTimeout(attributeTimer);
    const send = () => {
      clearTimeout(attributeTimer);
      attributeTimer = null;
      flushAttributeSave = null;
      queueWrite(() => fetch(`${routes.cart_update_url || '/cart/update'}.js`, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({attributes: {[match[1]]: field.value}}), keepalive: true})).catch(() => {});
    };
    if (delay) {
      attributeTimer = setTimeout(send, delay);
      flushAttributeSave = send;
    } else {
      send();
    }
  }
  document.addEventListener('input', (event) => {
    if (event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement) saveAttribute(event.target, 400);
  });
  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement) saveAttribute(event.target, 0);
  });

  /* ------------------------------------------------------------------------
     9. The age check's answer on the order (snippets/vh-age-gate.liquid, Theme settings → Vinhuset: Alderstjek):
        a cart with products carries _age_confirmed (the age) and _age_confirmed_at (when the visitor answered), so
        each order documents it under "Yderligere oplysninger". Checked with the cart's other rules (runSync) and
        written through the same queue: of two writes sent at once, Shopify can keep only one. Checkout waits for it.
     ------------------------------------------------------------------------ */
  function cookieValue(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function ageAnswered() {
    if (cookieValue('ageVerified') === 'true') return true;
    try {
      return window.sessionStorage.getItem('ageVerified') === 'true';
    } catch (e) {
      return false;
    }
  }

  // The age answer the cart should carry, or null when it carries it already or there is none to write
  function ageAttributes(cart) {
    const gate = config.ageGate || {};
    if (!gate.document || !cart || !cart.item_count || !ageAnswered()) return null;
    const age = String(gate.age || '');
    // Answered on the live site (Blockify) there is no time, only the answer
    const at = cookieValue('vh_age_confirmed_at') || '';
    const attributes = cart.attributes || {};
    if (attributes._age_confirmed === age && (!at || attributes._age_confirmed_at === at)) return null;
    return at ? {_age_confirmed: age, _age_confirmed_at: at} : {_age_confirmed: age};
  }

  async function documentAge(cart) {
    const update = ageAttributes(cart);
    if (!update) return;
    await queueWrite(async () => {
      const res = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({attributes: update})});
      if (res.ok) vh.lastCart = await res.json();
    });
  }

  // Answered with products already in the cart: written at once
  document.addEventListener('vh:age:confirmed', () => {
    if (window.theme && Number(window.theme.subtotal) === 0 && !(vh.lastCart && vh.lastCart.item_count)) return;
    scheduleSync({silent: true, delay: 0});
  });

  /* ------------------------------------------------------------------------
     10. "Accepter handelsbetingelser" (Theme settings → Vinhuset: Kurv), as Rebuy's Smart Cart does it on the current
         theme (F-130): the box is ticked on every page view, and a cart with products carries the attribute
         "Terms and Conditions: Accepted" from the first product on, not only when the cart form is sent (the MitID
         and fee checks go to checkout themselves). Unticking removes the attribute and locks "Til betaling" with
         Broadcast's message until the box is ticked again. The drawer and /cart can both have a box: all boxes on the
         page show the same choice, also after the drawer is drawn again. Written through the same queue as the
         cart's other rules (runSync); checkout waits for it.
     ------------------------------------------------------------------------ */
  const terms = config.terms || {};
  // a store without "Forvalgt" starts unticked; the message shows only once the customer has unticked the box
  let termsDeclined = !terms.prechecked;
  let termsTouched = false;

  function showTerms() {
    if (!terms.enable) return;
    document.querySelectorAll('[data-cart-acceptance-checkbox]').forEach((box) => {
      box.checked = !termsDeclined;
    });
    document.querySelectorAll('[data-cart-checkout-button]').forEach((fieldset) => {
      if (termsDeclined) fieldset.setAttribute('disabled', 'true');
      else fieldset.removeAttribute('disabled');
    });
    document.querySelectorAll('[data-terms-error-message]').forEach((message) => {
      const show = termsDeclined && termsTouched;
      if (show) message.innerText = (window.theme && window.theme.strings && window.theme.strings.cartAcceptanceError) || '';
      message.classList.toggle('is-expanded', show);
    });
  }

  // The terms attribute the cart should carry, or null when it carries it already. An empty value removes it, as
  // unticking does in the Smart Cart
  function termsAttributes(cart) {
    if (!terms.enable || !terms.attribute || !cart || !cart.item_count) return null;
    const wanted = termsDeclined ? '' : terms.value;
    const current = (cart.attributes || {})[terms.attribute] || '';
    return current === wanted ? null : {[terms.attribute]: wanted};
  }

  async function documentTerms(cart) {
    const update = termsAttributes(cart);
    if (!update) return;
    await queueWrite(async () => {
      const res = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({attributes: update})});
      if (res.ok) vh.lastCart = await res.json();
    });
  }

  document.addEventListener('change', (event) => {
    const box = event.target.closest && event.target.closest('[data-cart-acceptance-checkbox]');
    if (!box || !terms.enable) return;
    termsTouched = true;
    termsDeclined = !box.checked;
    showTerms();
    scheduleSync({silent: true, delay: 0});
  });
  // A box drawn again (the drawer after a change) comes back as the server rendered it: keep the customer's choice
  document.addEventListener('DOMContentLoaded', () => {
    if (!terms.enable) return;
    showTerms();
    const watch = new MutationObserver((records) => {
      const drawn = records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches('[data-cart-acceptance-checkbox]') || node.querySelector('[data-cart-acceptance-checkbox]'))));
      if (drawn) showTerms();
    });
    document.querySelectorAll('cart-drawer, [data-cart-page]').forEach((root) => watch.observe(root, {childList: true, subtree: true}));
  });
})();
